/**
 * Product Innovation Lab - Core Types
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロダクトコア型定義。共通エンティティ・ページネーション・エラー
 * @graph-connects none
 */
/**
 * `PortalRole` の全値を含む読み取り専用配列。
 *
 * 各 app の zod スキーマなどから core-types の SSOT を参照するために提供する。
 * core-types に zod 依存を持ち込まないため、配列のみを export する。
 * @graph-connects none
 */
export const PORTAL_ROLES = [
    'admin',
    'group_manager',
    'team_leader',
    'member',
    'architect',
    'dle',
    'tech_lead',
    'pmo',
    'external',
];
/** @graph-connects none */
const PORTAL_ROLE_SET = new Set(PORTAL_ROLES);
/**
 * 任意の値が組織ポジションとしての `PortalRole` かを判定する。
 * @graph-connects none
 */
export function isPortalRole(value) {
    return typeof value === 'string' && PORTAL_ROLE_SET.has(value);
}
/**
 * エンジニア組織（Product Innovation Div.）の `division` コード。
 *
 * workspace-pipeline が Google Workspace の orgUnitPath 先頭セグメントから同期する
 * 組織所属の SSOT。Product Innovation Lab の内部/外部判定はこの値を基準にする。
 * @graph-connects none
 */
export const ENGINEERING_DIVISION = 'pi_div';
/**
 * Product Innovation Lab の「内部メンバー（full access）」判定。
 *
 * 内部とみなす条件は次のいずれか:
 * - `division === 'pi_div'`（workspace-pipeline 同期の組織 SSOT に基づく所属判定）
 * - `member` / `external` 以外の明示付与ロールを持つ（アカウント管理で個別に
 *   権限を割り当てられたユーザー。`division` が空の役員等を救済する）
 *
 * `organizationId`(PDG/ACE) は workspace-pipeline が同期せず値が不揃いのため判定軸に
 * しない。`member` は初回ログイン時に全社員へ自動付与されるフォールバックロールなので、
 * これ単体では内部判定の根拠にしない（所属 `division` 側で判定する）。
 *
 * 複数ロール保持時は「`member`/`external` 以外のロールを1つでも持つか」の existential
 * チェック（優先順位ではない）。
 * @graph-connects none
 */
export function isInternalPortalMember(division, portalRoles) {
    if (division === ENGINEERING_DIVISION)
        return true;
    return (portalRoles ?? []).some((role) => role !== 'member' && role !== 'external');
}
/**
 * Product Innovation Lab の「PI Div 所属者・admin」判定（PI Div 専用機能のフルアクセス対象）。
 *
 * 条件は次のいずれか:
 * - `division === 'pi_div'`（PI Div 所属の社員）
 * - `admin` ロールを保持する（横断 admin。Div 不問）
 *
 * KPI / アカウント管理 / メンバー / GitHub Activity 等の PI Div 専用機能の開放対象を表す。
 * 横断 PMO（ac_div/mall_div/log_div × pmo）は false（`/projects` と `/my-page` のみ）。
 * フロント（`usePageAccess` / `canAccessPage`）とバックエンド（`requirePiDivOrAdmin`）が
 * 同一述語を参照するための SSOT。
 * @graph-connects none
 */
export function isPiDivOrAdmin(division, portalRoles) {
    return division === ENGINEERING_DIVISION || (portalRoles?.includes('admin') ?? false);
}
/**
 * `CrossDivPmoDivision` の全値を含む読み取り専用配列。
 * @graph-connects none
 */
export const CROSS_DIV_PMO_DIVISIONS = [
    'ac_div',
    'mall_div',
    'log_div',
];
/** @graph-connects none */
const CROSS_DIV_PMO_DIVISION_SET = new Set(CROSS_DIV_PMO_DIVISIONS);
/** @graph-connects none */
export const CROSS_DIV_PMO_SCOPE = {
    ac_div: {
        divisionLabel: 'aC Div',
        businesses: ['エアクロ', 'Spot Rental', 'エアクロメンズ'],
        badgeColor: '#DC4040',
    },
    mall_div: {
        divisionLabel: 'Mall Div',
        businesses: ['エアクロモール'],
        badgeColor: '#50BA52',
    },
    log_div: {
        // 注意: BQ 側 `pb_sheet_sync.division` の値は 'Log Div'（短縮形）。UI 側の divLabel は
        // 'Logi Div'（プロダクト固有の社内呼称）でフロント表示用。BQ の divisionLabel は WHERE 句で
        // 等価マッチに使うので絶対に変更しないこと。両者の対応は
        // `apps/web/product-innovation-lab/src/lib/cross-div-pmo-ui.ts` の `log_div.divLabel` 参照。
        divisionLabel: 'Log Div',
        businesses: ['AC PORT'],
        extraGroup: 'SCMG',
        badgeColor: '#56C3DA',
    },
};
/**
 * `division` コードが横断 PMO 対象 Div か判定する型ガード。
 * @graph-connects none
 */
export function isCrossDivPmoDivision(division) {
    return typeof division === 'string' && CROSS_DIV_PMO_DIVISION_SET.has(division);
}
/**
 * 横断 PMO ユーザー（pi_div 以外 × `pmo` ロールを保持）の `/projects` スコープを返す。
 * pi_div の PMO / admin / 非 PMO の非 pi_div 社員は null（横断スコープは適用しない）。
 *
 * `admin` ロールは横断対象外で常に全社閲覧（呼び出し側で null として扱う）。
 * 複数ロール保持時は「`pmo` を1つでも持つか」の existential チェック。
 * @graph-connects none
 */
export function resolveCrossDivPmoScope(division, portalRoles) {
    if (!(portalRoles?.includes('pmo') ?? false))
        return null;
    if (!isCrossDivPmoDivision(division))
        return null;
    return division;
}
/**
 * workspace.users.team_name（例: `ac_nbt` / `m_bdt` / `log_lpt`）を、Backlog 系テーブル
 * （`project_detail_table.responsible_team` / `pb_sheet_sync.responsible_team`）で使われる
 * 大文字ハイフン区切り表記（例: `NBT` / `M-BDT` / `LPT`）に変換する。
 *
 * 規約:
 *   - `ac_<x>`  → `<X>`（プレフィクスを取り除いて大文字化）
 *   - `log_<x>` → `<X>`（同上）
 *   - `m_<x>`   → `M-<X>`（Mall Div のみ pb_sheet_sync 側が `M-` プレフィクス）
 *
 * 期待する `division` 以外のプレフィクスを持つチームコード（旧称・別 Div の出向など）は
 * 変換できないため null を返す。
 * @graph-connects none
 */
export function mapWorkspaceTeamToResponsibleTeam(division, workspaceTeamName) {
    if (!workspaceTeamName)
        return null;
    const lower = workspaceTeamName.toLowerCase();
    const prefix = division === 'mall_div' ? 'm_' : division === 'log_div' ? 'log_' : 'ac_';
    if (!lower.startsWith(prefix))
        return null;
    const suffix = lower.substring(prefix.length).toUpperCase();
    if (suffix.length === 0)
        return null;
    return division === 'mall_div' ? `M-${suffix}` : suffix;
}
/**
 * `/projects` ページのスコープを user の (division, portalRoles, teamName) から決定する。
 *
 * - 非 pi_div × `pmo` を保持     → Div 全体スコープ（lockedTeam=null）
 * - 非 pi_div × `team_leader` を保持（`pmo` 非保持） → 所属チームに絞ったスコープ
 * - pi_div / admin / その他       → null（全社表示）
 *
 * 複数ロール保持時は「各保持ロールが許可するスコープの union」で考える。`pmo` の許可範囲
 * （Div 全体）は `team_leader` の許可範囲（自チームのみ）を包含するため、両方を保持していても
 * union は Div 全体に収束する。これは「pmo が team_leader に優先する」というランキングでは
 * なく、pmo 側のスコープが team_leader 側のスコープを包含する結果 union がそう収束するだけ。
 * 実装上は pmo を先に判定しているだけで、優先順位を意図したものではない。
 *
 * `team_leader` でも workspace.users.team_name が Div プレフィクスに一致しない場合
 * （未同期・例外 OU 配置）は null を返し、テーブル全件を返さない側に倒す（呼び出し元で
 * 403 もしくは「該当案件なし」として扱う）。
 * @graph-connects none
 */
export function resolveProjectScopedView(division, portalRoles, teamName) {
    if (!isCrossDivPmoDivision(division))
        return null;
    const roles = portalRoles ?? [];
    if (roles.includes('pmo')) {
        return { division, lockedTeam: null };
    }
    if (roles.includes('team_leader')) {
        const lockedTeam = mapWorkspaceTeamToResponsibleTeam(division, teamName);
        if (!lockedTeam)
            return null;
        return { division, lockedTeam };
    }
    return null;
}
/**
 * `/projects` のアクセスモードを user の (division, portalRoles, teamName) から決定する。
 *
 * `resolveProjectScopedView` を null/scope の 2 値で返すと、呼び出し側で
 * 「null = 全社閲覧」と扱われて非 pi_div × 非 admin のロールが意図せず全社案件を
 * 見られてしまう（例: `non-pi_div × team_leader` で teamName が Div プレフィクスに
 * 一致しないケース、`administration_office × pmo` 等）。
 *
 * この関数は 3 値を返し、呼び出し側で「空配列を返す」分岐を明示できるようにする。
 *
 * 規約:
 * - `pi_div` / `admin` を保持 → `unscoped`（全社案件）
 * - 非 pi_div × 非 admin で scope 取得成功 → `scoped`
 * - 非 pi_div × 非 admin で scope 取得失敗 → `empty`（全社閲覧へフォールスルーしない）
 *
 * @graph-connects none
 */
export function resolveProjectAccessMode(division, portalRoles, teamName) {
    if (isPiDivOrAdmin(division, portalRoles)) {
        return { kind: 'unscoped' };
    }
    const scope = resolveProjectScopedView(division, portalRoles, teamName);
    if (scope)
        return { kind: 'scoped', scope };
    return { kind: 'empty' };
}
/**
 * Firestore `users.productPortalRoles`（配列, SSOT）優先で読み、未 backfill のドキュメント
 * は旧 `users.productPortalRole`（scalar）にフォールバックする共通ヘルパー。
 * 複数ロール保持時の代表値導出は `derivePrimaryPortalRole` と同じ優先順位（admin優先、
 * 以降 `PORTAL_ROLES` 宣言順）に従う。
 * @graph-connects none
 */
export function resolvePortalRoleFromFirestore(productPortalRoles, legacyProductPortalRole) {
    const roles = Array.isArray(productPortalRoles) ? productPortalRoles.filter(isPortalRole) : [];
    const primary = derivePrimaryPortalRole(roles);
    if (primary)
        return primary;
    return isPortalRole(legacyProductPortalRole) ? legacyProductPortalRole : undefined;
}
/**
 * ポータル権限定義（中央集権管理）
 *
 * 各業務権限に対してアクセス可能なロールを定義する。
 * アプリゲート（特定アプリへのアクセス可否）はこの定義に追加せず、
 * 各アプリ側の middleware と Firestore `app_access` collection で扱う。
 *
 * @graph-connects none
 */
export const PORTAL_PERMISSIONS = {
    /** アカウント管理（pmo は member ロールのみ追加/編集/削除可能。詳細は apps/api/product 側で enforce） */
    MANAGE_ACCOUNTS: ['admin', 'group_manager', 'pmo'],
    /** QA評価閲覧（team_leaderはQAチーム所属のみ） */
    VIEW_QA: {
        roles: ['admin', 'group_manager'],
        teamConditions: {
            team_leader: ['QA team'],
        },
    },
    /** メンバー管理閲覧 */
    VIEW_MEMBERS: ['admin', 'group_manager', 'team_leader', 'pmo'],
    /** KPIチャットアシスタント */
    VIEW_KPI_CHAT: ['admin', 'group_manager', 'team_leader'],
    /** プロジェクト管理閲覧 */
    VIEW_PROJECTS: ['admin', 'group_manager', 'team_leader', 'dle', 'pmo'],
    /**
     * PL/主担当看板閲覧（全 PortalRole + external）。
     *
     * Product Innovation Lab を全社員に開放するための入口ページ。各社員が自分の担当案件
     * （PL = `project_detail_table.assignee`、主担当 = `pb_sheet_sync.main_worker`）を
     * `users.nickName` でマッチングして閲覧する。portalRole 未設定者も `auth/me` 側で
     * 'external' にフォールバックされるため、ここでは全ロールを許可する。
     */
    VIEW_MY_PROJECTS_BOARD: [
        'admin',
        'group_manager',
        'team_leader',
        'member',
        'architect',
        'dle',
        'tech_lead',
        'pmo',
        'external',
    ],
    /** PMO看板閲覧・編集（PMOチーム = pi_pmot 所属者 or 管理者のみ） */
    VIEW_PMO_BOARD: {
        roles: ['admin', 'group_manager'],
        teamConditions: {
            team_leader: ['pi_pmot'],
            member: ['pi_pmot'],
            pmo: ['pi_pmot'],
            dle: ['pi_pmot'],
            architect: ['pi_pmot'],
            tech_lead: ['pi_pmot'],
        },
    },
    /** DB辞書編集 */
    EDIT_DB_DICTIONARY: ['admin', 'architect', 'dle', 'tech_lead'],
    /** バグ評価閲覧 */
    VIEW_BUG_EVALUATION: ['admin', 'group_manager', 'team_leader', 'architect', 'tech_lead'],
    /**
     * QA責任評価閲覧（参照）。チームリーダーはQAチームに限らず全 team_leader が対象。
     * ロールは組織ポジションのみを表現し「PI Div 所属か」はこの権限では表せないため、
     * 実際の PI Div スコープ（エンジニア組織の team_leader に限定）は呼び出し側の
     * `isPiDivOrAdmin`（web: canAccessPage / api: requirePiDivOrAdmin）で別途 enforce する。
     */
    VIEW_QA_RESPONSIBILITY: [
        'admin',
        'group_manager',
        'architect',
        'tech_lead',
        'team_leader',
    ],
    /** QA責任評価の確定（admin/architect 無条件、team_leaderはQAチーム所属のみ） */
    CONFIRM_QA_RESPONSIBILITY: {
        roles: ['admin', 'architect'],
        teamConditions: {
            team_leader: ['QA team'],
        },
    },
    /** エンジニア評価閲覧（adminは全件、group_manager/team_leaderは自グループ/チームのみ） */
    VIEW_ENGINEER_EVALUATION: ['admin', 'group_manager', 'team_leader'],
    /** インフラ費用閲覧（管理者・チームリーダー無条件、SREチームメンバーのみ条件付き） */
    VIEW_INFRA_COST: {
        roles: ['admin', 'group_manager', 'team_leader'],
        teamConditions: {
            member: ['pi_sret', 'sret'],
        },
    },
};
/**
 * 権限チェック（ロール + チーム条件対応）。
 *
 * 複数ロール保持時は「保持しているロールの中に許可された役割があるか」の existential
 * チェック（`.some()`）であり、「広いロールが勝つ」という優先順位の概念はない。
 * @graph-connects none
 */
export function checkPermission(userRoles, permission, userTeamName) {
    if (!userRoles || userRoles.length === 0)
        return false;
    const rule = PORTAL_PERMISSIONS[permission];
    // 単純なロール配列
    if (Array.isArray(rule)) {
        return userRoles.some((role) => rule.includes(role));
    }
    // 条件付き権限
    const conditional = rule;
    if (userRoles.some((role) => conditional.roles.includes(role))) {
        return true;
    }
    if (!userTeamName)
        return false;
    return userRoles.some((role) => conditional.teamConditions[role]?.includes(userTeamName));
}
/**
 * 保持ロール配列から後方互換の単一 `PortalRole` を導出する（移行期間の transitional
 * ヘルパー。他アプリ共有 Firestore `productPortalRole`（単数）互換書き込み、旧クライアント
 * 向け derived レスポンス用）。
 *
 * - `admin` を保持していれば常に `admin`（横断 admin は他ロールと同時付与されても admin 優先）
 * - それ以外は `PORTAL_ROLES` の宣言順で最初に一致したロール
 * - 保持ロールが空/未設定なら undefined
 * @graph-connects none
 */
export function derivePrimaryPortalRole(portalRoles) {
    if (!portalRoles || portalRoles.length === 0)
        return undefined;
    if (portalRoles.includes('admin'))
        return 'admin';
    return PORTAL_ROLES.find((role) => portalRoles.includes(role));
}
/**
 * 「保持ロールが member 以外を含まないか」の判定。pmo によるアカウント操作・
 * impersonate 制約（対象は member ロールのみ操作可）で使う共通ヘルパー。
 *
 * `roles が空、または ['member'] のみ` を許可する existential チェックであり、
 * 「member を含むか」ではない点に注意する。「member を含むか」で判定すると
 * `['member', 'admin']` のような組み合わせが素通りしてしまう抜け穴になる。
 * @graph-connects none
 */
export function isMemberOnlyRoleSet(portalRoles) {
    if (!portalRoles || portalRoles.length === 0)
        return true;
    return portalRoles.every((role) => role === 'member');
}
//# sourceMappingURL=portal.js.map